/**
 * Storage utility functions for localStorage and sessionStorage
 */

/**
 * Safely get item from localStorage
 */
export function getFromLocalStorage<T = string>(
  key: string,
  defaultValue?: T,
): T | null {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue ?? null;
    }
    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(item) as T;
    } catch {
      return item as T;
    }
  } catch (error) {
    console.error(`Error reading from localStorage (key: ${key}):`, error);
    return defaultValue ?? null;
  }
}

/**
 * Safely set item in localStorage
 */
export function saveToLocalStorage(key: string, value: unknown): boolean {
  try {
    const stringValue =
      typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    console.error(`Error writing to localStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Safely remove item from localStorage
 */
export function removeFromLocalStorage(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Safely get item from sessionStorage
 */
export function getFromSessionStorage<T = string>(
  key: string,
  defaultValue?: T,
): T | null {
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return defaultValue ?? null;
    }
    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(item) as T;
    } catch {
      return item as T;
    }
  } catch (error) {
    console.error(`Error reading from sessionStorage (key: ${key}):`, error);
    return defaultValue ?? null;
  }
}

/**
 * Safely set item in sessionStorage
 */
export function saveToSessionStorage(key: string, value: unknown): boolean {
  try {
    const stringValue =
      typeof value === 'string' ? value : JSON.stringify(value);
    sessionStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    console.error(`Error writing to sessionStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Safely remove item from sessionStorage
 */
export function removeFromSessionStorage(key: string): boolean {
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from sessionStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Clear all items from localStorage
 */
export function clearLocalStorage(): boolean {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
}

/**
 * Clear all items from sessionStorage
 */
export function clearSessionStorage(): boolean {
  try {
    sessionStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing sessionStorage:', error);
    return false;
  }
}
